import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import cookie from "react-cookies";
import {
  deleteQualification,
  getCandidateProfile,
  patchCandidateProfile,
  requestQualificationUpload,
  confirmQualificationUpload,
} from "../../services/academyApi";
import { putFileWithProgress } from "../../services/creatorApi";
import { TrainingHero, TrainingLoading, TrainingProgressBar } from "../academy/AcademyShared";
import {
  ALLOWED_UPLOAD_TYPES,
  AVAILABILITY_OPTIONS,
  EMPLOYED_OPTIONS,
  CV_UPLOAD_ACCEPT,
  CV_UPLOAD_TYPES,
  DOC_TYPE_LABELS,
  DOC_TYPE_OPTIONS,
  DRIVER_LICENSE_OPTIONS,
  MAX_UPLOAD_BYTES,
  PROGRAM_OPTIONS,
  QUALIFICATION_UPLOAD_ACCEPT,
  SA_PROVINCES,
  TARGET_ROLE_OPTIONS,
} from "./profileOptions";
import MeLayout from "./MeLayout";
import {
  isSafeCreatorNext,
  missingCreatorContactFields,
} from "./creator/creatorContact";

function formatDateInput(val) {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const PERSON_NAME_PATTERN =
  /^[\p{L}\p{M}]+(?:[ '\u2019-][\p{L}\p{M}]+)*$/u;

function normalizePersonName(value) {
  return String(value || "").normalize("NFC").trim().replace(/\s+/g, " ");
}

function validatePersonName(value, label) {
  const name = normalizePersonName(value);
  if (!name) return `${label} is required.`;
  if (name.length > 50) return `${label} must be 50 characters or fewer.`;
  if (!PERSON_NAME_PATTERN.test(name)) {
    return `${label} may contain letters, spaces, apostrophes, and hyphens only.`;
  }
  return null;
}

function ProfileField({ label, hint, required, children }) {
  return (
    <label className="profile-field">
      <span className="profile-field__label">
        {label}
        {required && <span className="profile-field__req"> *</span>}
      </span>
      {children}
      {hint && <span className="profile-field__hint">{hint}</span>}
    </label>
  );
}

export default function MeProfile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("next");
  const creatorReturn = isSafeCreatorNext(returnTo) ? returnTo : null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [completionPct, setCompletionPct] = useState(0);
  const [qualifications, setQualifications] = useState([]);
  const [latestCv, setLatestCv] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [form, setForm] = useState({});
  const [uploadForm, setUploadForm] = useState({
    doc_type: "drivers_license",
    title: "",
    issuing_body: "",
    issued_at: "",
    expires_at: "",
    file: null,
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCandidateProfile();
      setForm({
        ...data.candidate,
        date_of_birth: formatDateInput(data.candidate.date_of_birth),
        has_pdp: data.candidate.has_pdp === true,
      });
      setQualifications(data.qualifications || []);
      setLatestCv(data.latest_cv || null);
      setCompletionPct(data.profile_completion_pct || 0);
    } catch {
      setFeedback({ type: "error", message: "Could not load your profile." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/profile";
      return;
    }
    loadProfile();
  }, [loadProfile]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const firstname = normalizePersonName(form.firstname);
    const lastname = normalizePersonName(form.lastname);
    const nameError =
      validatePersonName(firstname, "First name") ||
      validatePersonName(lastname, "Last name");
    if (nameError) {
      setFeedback({ type: "error", message: nameError });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const payload = {
        firstname,
        lastname,
        mobilenumber: form.mobilenumber?.trim(),
        idnumber: form.idnumber?.trim(),
        date_of_birth: form.date_of_birth || null,
        city: form.city?.trim(),
        province: form.province || null,
        suburb: form.suburb?.trim(),
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        availability: form.availability || null,
        currently_employed:
          form.currently_employed === true
            ? true
            : form.currently_employed === false
              ? false
              : null,
        driver_license_code: form.driver_license_code || null,
        has_pdp: form.has_pdp === true,
        target_role: form.target_role || null,
        primary_academy_slug: form.primary_academy_slug || null,
        promoterlicense: form.promoterlicense?.trim(),
      };
      const res = await patchCandidateProfile(payload);
      setForm((f) => ({ ...f, ...res.candidate, date_of_birth: formatDateInput(res.candidate.date_of_birth) }));
      setCompletionPct(res.profile_completion_pct || 0);
      if (res.candidate.firstname) {
        cookie.save("candidateName", res.candidate.firstname, { path: "/", maxAge: 86400, secure: true });
      }
      const contactMissing = missingCreatorContactFields(res.candidate);
      if (creatorReturn && contactMissing.length === 0) {
        navigate(creatorReturn);
        return;
      }
      setFeedback({
        type: "success",
        message: creatorReturn
          ? "Profile saved. Add your name and mobile number to continue to Creator."
          : "Profile saved.",
      });
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "Could not save profile." });
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async ({ doc_type, title, file, issuing_body, issued_at, expires_at }) => {
    const prep = await requestQualificationUpload({
      doc_type,
      title,
      issuing_body,
      issued_at,
      expires_at,
      file: {
        name: file.name,
        type: file.type,
        size: file.size,
      },
    });

    const contentType = file.type || "application/octet-stream";
    await putFileWithProgress(prep.upload_url, file, { contentType }).promise;

    await confirmQualificationUpload({
      id: prep.qualification?.id,
      s3_key: prep.s3_key,
      doc_type,
      title,
      issuing_body,
      issued_at,
      expires_at,
      file_name: file.name,
      mime_type: contentType,
      file_size_bytes: file.size,
    });
  };

  const uploadCv = async (e) => {
    e.preventDefault();
    if (!cvFile) return;
    if (!CV_UPLOAD_TYPES.includes(cvFile.type)) {
      setFeedback({ type: "error", message: "CV must be PDF, Word, JPG, or PNG." });
      return;
    }
    if (cvFile.size > MAX_UPLOAD_BYTES) {
      setFeedback({ type: "error", message: "File must be 10 MB or smaller." });
      return;
    }
    setUploadingCv(true);
    setFeedback(null);
    try {
      await uploadDocument({
        doc_type: "cv",
        title: "Latest CV",
        file: cvFile,
      });
      setCvFile(null);
      await loadProfile();
      setFeedback({
        type: "success",
        message: latestCv ? "CV replaced with your latest version." : "CV uploaded.",
      });
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "CV upload failed." });
    } finally {
      setUploadingCv(false);
    }
  };

  const uploadQualification = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return;
    if (!ALLOWED_UPLOAD_TYPES.includes(uploadForm.file.type)) {
      setFeedback({ type: "error", message: "Use PDF, JPG, or PNG files only." });
      return;
    }
    if (uploadForm.file.size > MAX_UPLOAD_BYTES) {
      setFeedback({ type: "error", message: "File must be 10 MB or smaller." });
      return;
    }
    setUploading(true);
    setFeedback(null);
    try {
      await uploadDocument({
        doc_type: uploadForm.doc_type,
        title: uploadForm.title.trim() || DOC_TYPE_LABELS[uploadForm.doc_type],
        issuing_body: uploadForm.issuing_body?.trim(),
        issued_at: uploadForm.issued_at || null,
        expires_at: uploadForm.expires_at || null,
        file: uploadForm.file,
      });

      setUploadForm({
        doc_type: "drivers_license",
        title: "",
        issuing_body: "",
        issued_at: "",
        expires_at: "",
        file: null,
      });
      await loadProfile();
      setFeedback({ type: "success", message: "Qualification uploaded." });
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "Upload failed." });
    } finally {
      setUploading(false);
    }
  };

  const removeQualification = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await deleteQualification(id);
      setQualifications((q) => q.filter((x) => x.id !== id));
      setFeedback({ type: "success", message: "Document removed." });
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "Could not remove document." });
    }
  };

  if (loading) {
    return (
      <MeLayout title="My profile">
        <TrainingLoading label="Loading profile..." />
      </MeLayout>
    );
  }

  const otherQualifications = qualifications.filter((q) => q.doc_type !== "cv");

  return (
    <MeLayout title="My profile">
      <TrainingHero
        eyebrow="Career profile"
        title="Details recruiters see"
        subtitle="Add your personal information and upload current qualifications. You can update these anytime."
      />

      {creatorReturn ? (
        <div className="creator-contact-banner" role="status">
          <div>
            <p className="creator-contact-banner__title">Creator contact details needed</p>
            <p className="creator-contact-banner__copy">
              Add your first name, last name, and mobile number, then save. We will send you
              back to Creator.
            </p>
          </div>
        </div>
      ) : null}

      <TrainingProgressBar value={completionPct} label="Profile completeness" />

      {feedback && (
        <div className={`training-callout training-feedback training-feedback-${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      <form className="training-card profile-form" onSubmit={saveProfile}>
        <h2 className="profile-section-title">Personal details</h2>
        <p className="profile-section-desc">Required for job matching and compliance checks.</p>

        <div className="profile-form-grid">
          <ProfileField label="First name" required>
            <input
              className="profile-input"
              value={form.firstname || ""}
              onChange={(e) => setField("firstname", e.target.value)}
              autoComplete="given-name"
              maxLength={50}
              required
            />
          </ProfileField>
          <ProfileField label="Last name" required>
            <input
              className="profile-input"
              value={form.lastname || ""}
              onChange={(e) => setField("lastname", e.target.value)}
              autoComplete="family-name"
              maxLength={50}
              required
            />
          </ProfileField>
          <ProfileField label="Mobile number" required hint="e.g. 082 123 4567">
            <input
              className="profile-input"
              type="tel"
              value={form.mobilenumber || ""}
              onChange={(e) => setField("mobilenumber", e.target.value)}
              autoComplete="tel"
            />
          </ProfileField>
          <ProfileField label="Email" hint="Sign-in email (cannot be changed here)">
            <input className="profile-input profile-input--readonly" value={form.email || ""} readOnly />
          </ProfileField>
          <ProfileField label="SA ID or passport number" required>
            <input
              className="profile-input"
              value={form.idnumber || ""}
              onChange={(e) => setField("idnumber", e.target.value)}
            />
          </ProfileField>
          <ProfileField label="Date of birth">
            <input
              className="profile-input"
              type="date"
              value={form.date_of_birth || ""}
              onChange={(e) => setField("date_of_birth", e.target.value)}
            />
          </ProfileField>
        </div>

        <h2 className="profile-section-title">Location</h2>
        <div className="profile-form-grid">
          <ProfileField label="City / town" required>
            <input className="profile-input" value={form.city || ""} onChange={(e) => setField("city", e.target.value)} />
          </ProfileField>
          <ProfileField label="Province" required>
            <select className="profile-input" value={form.province || ""} onChange={(e) => setField("province", e.target.value)}>
              <option value="">Select province</option>
              {SA_PROVINCES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </ProfileField>
          <ProfileField label="Suburb / area" hint="Optional">
            <input className="profile-input" value={form.suburb || ""} onChange={(e) => setField("suburb", e.target.value)} />
          </ProfileField>
        </div>

        <h2 className="profile-section-title">Career preferences</h2>
        <div className="profile-form-grid">
          <ProfileField label="Target role" required>
            <select className="profile-input" value={form.target_role || ""} onChange={(e) => setField("target_role", e.target.value)}>
              <option value="">Select role</option>
              {TARGET_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ProfileField>
          <ProfileField label="Primary program">
            <select
              className="profile-input"
              value={form.primary_academy_slug || ""}
              onChange={(e) => setField("primary_academy_slug", e.target.value)}
            >
              {PROGRAM_OPTIONS.map((o) => (
                <option key={o.value || "none"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ProfileField>
          <ProfileField label="Years in logistics" hint="0 if you are starting out">
            <input
              className="profile-input"
              type="number"
              min="0"
              max="50"
              value={form.years_experience ?? ""}
              onChange={(e) => setField("years_experience", e.target.value)}
            />
          </ProfileField>
          <ProfileField label="Availability">
            <select className="profile-input" value={form.availability || ""} onChange={(e) => setField("availability", e.target.value)}>
              <option value="">Select availability</option>
              {AVAILABILITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ProfileField>
          <ProfileField label="Currently employed">
            <select
              className="profile-input"
              value={
                form.currently_employed === true
                  ? "yes"
                  : form.currently_employed === false
                    ? "no"
                    : ""
              }
              onChange={(e) => {
                const v = e.target.value;
                setField(
                  "currently_employed",
                  v === "yes" ? true : v === "no" ? false : null
                );
              }}
            >
              <option value="">Select</option>
              {EMPLOYED_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ProfileField>
        </div>

        <h2 className="profile-section-title">Licences & permits</h2>
        <div className="profile-form-grid">
          <ProfileField label="Driver's licence code">
            <select
              className="profile-input"
              value={form.driver_license_code || ""}
              onChange={(e) => setField("driver_license_code", e.target.value)}
            >
              {DRIVER_LICENSE_OPTIONS.map((o) => (
                <option key={o.value || "empty"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ProfileField>
          <ProfileField label="PDP (Professional driving permit)">
            <label className="profile-checkbox">
              <input
                type="checkbox"
                checked={form.has_pdp === true}
                onChange={(e) => setField("has_pdp", e.target.checked)}
              />
              <span>I hold a valid PDP</span>
            </label>
          </ProfileField>
          <ProfileField label="PrDP category" hint="If applicable, e.g. Goods">
            <input
              className="profile-input"
              value={form.promoterlicense || ""}
              onChange={(e) => setField("promoterlicense", e.target.value)}
            />
          </ProfileField>
        </div>

        <button type="submit" className="training-btn-primary profile-save-btn" disabled={saving}>
          {saving ? "Saving..." : "Save profile"}
        </button>
      </form>

      <section className="training-card profile-cv-section">
        <h2 className="profile-section-title">Latest CV</h2>
        <p className="profile-section-desc">
          Upload your most recent CV so recruiters can review your experience. PDF or Word preferred, max 10 MB.
          Uploading again replaces your previous CV.
        </p>

        {latestCv ? (
          <div className="profile-cv-current">
            <div>
              <strong>{latestCv.file_name}</strong>
              <span className="profile-doc-item__meta">
                Uploaded {new Date(latestCv.created_at).toLocaleDateString()}
                {latestCv.file_size_bytes
                  ? ` · ${Math.max(1, Math.round(latestCv.file_size_bytes / 1024))} KB`
                  : ""}
              </span>
            </div>
            <button type="button" className="profile-doc-remove" onClick={() => removeQualification(latestCv.id)}>
              Remove
            </button>
          </div>
        ) : (
          <p className="profile-cv-empty">No CV uploaded yet.</p>
        )}

        <form className="profile-cv-form" onSubmit={uploadCv}>
          <ProfileField label={latestCv ? "Replace with a new CV" : "Upload your CV"} required>
            <input
              className="profile-file-input"
              type="file"
              accept={CV_UPLOAD_ACCEPT}
              onChange={(e) => setCvFile(e.target.files?.[0] || null)}
            />
          </ProfileField>
          <button type="submit" className="training-btn-primary" disabled={uploadingCv || !cvFile}>
            {uploadingCv ? "Uploading..." : latestCv ? "Replace CV" : "Upload CV"}
          </button>
        </form>
      </section>

      <section className="training-card profile-upload-section">
        <h2 className="profile-section-title">Other qualifications</h2>
        <p className="profile-section-desc">
          Licences, certificates, and matric. PDF or photo (JPG/PNG), max 10 MB each.
        </p>

        {otherQualifications.length > 0 && (
          <ul className="profile-doc-list">
            {otherQualifications.map((q) => (
              <li key={q.id} className="profile-doc-item">
                <div>
                  <strong>{q.title}</strong>
                  <span className="profile-doc-item__meta">
                    {DOC_TYPE_LABELS[q.doc_type] || q.doc_type}
                    {q.issued_at ? ` · Issued ${new Date(q.issued_at).toLocaleDateString()}` : ""}
                    {q.expires_at ? ` · Expires ${new Date(q.expires_at).toLocaleDateString()}` : ""}
                  </span>
                  <span className="profile-doc-item__file">{q.file_name}</span>
                </div>
                <button type="button" className="profile-doc-remove" onClick={() => removeQualification(q.id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <form className="profile-upload-form" onSubmit={uploadQualification}>
          <div className="profile-form-grid">
            <ProfileField label="Document type" required>
              <select
                className="profile-input"
                value={uploadForm.doc_type}
                onChange={(e) => setUploadForm((u) => ({ ...u, doc_type: e.target.value }))}
              >
                {DOC_TYPE_OPTIONS.filter((o) => o.value !== "cv").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </ProfileField>
            <ProfileField label="Title" hint="e.g. Code 14 licence, NQF Level 4">
              <input
                className="profile-input"
                value={uploadForm.title}
                onChange={(e) => setUploadForm((u) => ({ ...u, title: e.target.value }))}
                placeholder={DOC_TYPE_LABELS[uploadForm.doc_type]}
              />
            </ProfileField>
            <ProfileField label="Issuing body" hint="Optional">
              <input
                className="profile-input"
                value={uploadForm.issuing_body}
                onChange={(e) => setUploadForm((u) => ({ ...u, issuing_body: e.target.value }))}
                placeholder="e.g. RTMC, university name"
              />
            </ProfileField>
            <ProfileField label="Issue date">
              <input
                className="profile-input"
                type="date"
                value={uploadForm.issued_at}
                onChange={(e) => setUploadForm((u) => ({ ...u, issued_at: e.target.value }))}
              />
            </ProfileField>
            <ProfileField label="Expiry date" hint="For licences and PDP">
              <input
                className="profile-input"
                type="date"
                value={uploadForm.expires_at}
                onChange={(e) => setUploadForm((u) => ({ ...u, expires_at: e.target.value }))}
              />
            </ProfileField>
            <ProfileField label="File" required>
              <input
                className="profile-file-input"
                type="file"
                accept={QUALIFICATION_UPLOAD_ACCEPT}
                onChange={(e) => setUploadForm((u) => ({ ...u, file: e.target.files?.[0] || null }))}
              />
            </ProfileField>
          </div>
          <button type="submit" className="training-btn-secondary" disabled={uploading || !uploadForm.file}>
            {uploading ? "Uploading..." : "Upload document"}
          </button>
        </form>
      </section>
    </MeLayout>
  );
}
